import { useRouter } from "next/router";
import { useQuery } from "@tanstack/react-query";
import { CommonAPI } from "../../types";
import {
  Card,
  CardGrid,
  FixedToolbarItem,
  FlexToolbarItem,
  Histogram,
  Toolbar,
  useAATheme,
} from "@bagel-web/components";
import { Form, Spinner } from "react-bootstrap";
import { useMemo, useState } from "react";
import {
  getFunctionNames,
  getSessionHistogramDataByFunction,
} from "./dataTransformations";
import FunctionSelect from "./FunctionSelect";

const AgentHistogram = ({
  sessionId,
  agentId,
  commonAPI,
}: {
  sessionId: string;
  agentId: string;
  commonAPI: CommonAPI;
}) => {
  const [selectedFunctionNames, setSelectedFunctionNames] = useState<
    Array<string>
  >([]);
  const [errorsOnly, setErrorsOnly] = useState(false);

  const { data: sessionStats, isPending } = useQuery({
    queryKey: ["session-histogram", sessionId],
    queryFn: () => commonAPI.fetchSessionStats({ sessionId, numBuckets: 50 }),
  });

  const { isDarkMode } = useAATheme();
  const structuredSessionStats = useMemo(() => {
    return getSessionHistogramDataByFunction({
      agentId,
      sessionStats,
      selectedFunctionNames,
      errorsOnly,
      isDarkMode,
    });
  }, [sessionStats, agentId, selectedFunctionNames, errorsOnly, isDarkMode]);

  const router = useRouter();

  const handleClickElement = async (dataSetIndex: number, index: number) => {
    const dataPeriod =
      structuredSessionStats?.datasets?.[dataSetIndex]?.data[index];
    const agentId = dataPeriod?.agentId;
    const functionName = dataPeriod?.functionName;
    const startTime = dataPeriod?.start.timeStamp;
    const endTime = dataPeriod?.end.timeStamp;
    const hasError = dataPeriod?.isError;

    if (agentId && startTime && endTime) {
      const momentSummary = await commonAPI.fetchAgentMomentList({
        sessionId,
        agentId,
        startTime,
        endTime,
        functionName,
        hasError,
      });
      if (momentSummary.moments && momentSummary.moments[0]) {
        const momentId = momentSummary.moments[0].moment_id;
        const foreignSessionId =
          momentSummary.moments[0].session_id !== sessionId
            ? momentSummary.moments[0].session_id
            : undefined;
        router.replace(
          `/sessions/${sessionId}/agent/${agentId}?momentId=${momentId}${foreignSessionId ? `&foreignSessionId=${momentSummary.moments[0].session_id}` : ""}`
        );
      }
    }
  };

  const allFunctionNames = useMemo(() => {
    const funcNames = new Set(getFunctionNames(sessionStats, agentId, false));
    getFunctionNames(sessionStats, agentId, true).forEach((funcName) =>
      funcNames.add(funcName)
    );
    return Array.from(funcNames);
  }, [sessionStats, agentId]);

  return (
    <CardGrid className="mt-3" maxColumns={1}>
      <Card>
        <Toolbar className="mb-2">
          <FlexToolbarItem>
            <h5>Agent Function Calls</h5>
          </FlexToolbarItem>
          <FixedToolbarItem>
            <div>
              <FunctionSelect
                functionNames={allFunctionNames}
                onChange={(e) => {
                  setSelectedFunctionNames(e.target.value);
                }}
              />
            </div>
          </FixedToolbarItem>
          <FixedToolbarItem>
            <Form.Check
              type="switch"
              checked={errorsOnly}
              onChange={(e) => setErrorsOnly(e.target.checked)}
              label="Errors only"
            ></Form.Check>
          </FixedToolbarItem>
        </Toolbar>
        {isPending && <Spinner />}
        {!isPending && (
          <Histogram
            id="agent-histogram"
            data={structuredSessionStats}
            onClickElement={handleClickElement}
          />
        )}
      </Card>
    </CardGrid>
  );
};

export default AgentHistogram;
